import { z } from 'zod';

/**
 * The **structural** ceiling on a message body, in characters.
 *
 * It is not the product's limit — that is `chat.maxMessageLength`, an
 * operator-tunable setting resolved at write time (U9), and the service is what
 * enforces it. This is the ceiling on what the *transport* will carry at all: a
 * bound the schema can refuse cheaply, before a megabyte of text reaches a
 * service, a settings lookup and a database.
 *
 * **It is deliberately the same number as the setting's own `max`**, and the
 * setting definition imports it from here rather than repeating it. If this were
 * the smaller of the two, an operator could configure a ceiling the contract
 * would then refuse — a setting that silently does not apply, which is the
 * failure U9 exists to prevent.
 */
export const MESSAGE_BODY_MAX_LENGTH = 10_000;

/**
 * Sending a message.
 *
 * **The body is the only field.** The conversation is in the path, the sender is
 * the token, and the clock is the server's (N3) — a client that could name any
 * of the three would be a client that could write as somebody else, into
 * somebody else's conversation, at a time it chose.
 *
 * `.trim()` runs before the length check, so a body of spaces and newlines is
 * refused as empty rather than stored as a message that draws as a blank bubble.
 * The trimmed value is what is stored: trimming at read time instead would mean
 * the length the ceiling measured and the length that was kept are different
 * numbers.
 */
export const MessageCreateSchema = z
  .object({
    body: z.string().trim().min(1).max(MESSAGE_BODY_MAX_LENGTH),
  })
  .strict();

export type MessageCreate = z.infer<typeof MessageCreateSchema>;
